import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import SuperAdminSidebar from "../SuperAdminSidebar";
import ManagerSidebar from "../ManagerSidebar";
import LeaderSidebar from "../LeaderSidebar";
import MemberSidebar from "../MemberSidebar";
import AdminNavbar from './AdminNavbar';
import './AdminChatViewer.css';

const BASE = 'https://skcapi.btcjapan.co';
const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
};

const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.replace(' ', 'T'));
    return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

const formatBubbleTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.replace(' ', 'T'));
    return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit'
    });
};

const AdminChatViewer = () => {
    const userType = localStorage.getItem("usertype");
    const [threads, setThreads]   = useState([]);
    const [loadingT, setLoadingT] = useState(true);
    const [search, setSearch]     = useState('');
    const [active, setActive]     = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingM, setLoadingM] = useState(false);

    const meId = parseInt(localStorage.getItem('id') || localStorage.getItem('user_id') || '0', 10);

    // -------- Load all threads --------
    const loadThreads = useCallback(async () => {
        setLoadingT(true);
        try {
            const res = await axios.get(`${BASE}/chat_admin_view.php`, {
                headers: authHeader(),
                params: search ? { q: search } : {}
            });
            if (res.data.success) setThreads(res.data.data);
        } catch (err) {
            console.error('loadThreads error:', err);
        } finally {
            setLoadingT(false);
        }
    }, [search]);

    useEffect(() => {
        const t = setTimeout(loadThreads, 250);   // debounce search
        return () => clearTimeout(t);
    }, [loadThreads]);

    // -------- Load messages when a thread is selected --------
    const loadMessages = useCallback(async (threadId) => {
        setLoadingM(true);
        try {
            const res = await axios.get(`${BASE}/chat_admin_view.php`, {
                headers: authHeader(),
                params: { thread_id: threadId, limit: 200 }
            });
            if (res.data.success) setMessages(res.data.data);
        } catch (err) {
            console.error('loadMessages error:', err);
            setMessages([]);
        } finally {
            setLoadingM(false);
        }
    }, []);

    useEffect(() => {
        if (active) loadMessages(active.thread_id);
        else setMessages([]);
    }, [active, loadMessages]);

    // -------- Refresh button also re-fetches messages --------
    const handleRefresh = () => {
        loadThreads();
        if (active) loadMessages(active.thread_id);
    };

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <SuperAdminSidebar />}
                {userType === "leader" && <LeaderSidebar />}
                {userType === "manager" && <ManagerSidebar />}
                {userType === "member" && <MemberSidebar />}
            </div>

            <div className="admin-content">
                <AdminNavbar />

                <div className="main-content">
                    <div className="admin-chat-viewer">
                        <header className="acv-hero">
                            <div>
                                <div className="acv-eyebrow">
                                    <span className="acv-dot" />
                                    SUPER ADMIN AUDIT VIEW
                                </div>
                                <h1>All Conversations</h1>
                                <p>Read-only view of every chat thread across the system. Use the search to find a user or group.</p>
                            </div>
                            <div className="acv-hero-actions">
                                <button className="acv-refresh-btn" onClick={handleRefresh}>↻ Refresh</button>
                            </div>
                        </header>

                        <div className="acv-body">
                            {/* ------- Thread list ------- */}
                            <aside className="acv-sidebar">
                                <div className="acv-search-wrap">
                                    <input
                                        className="acv-search"
                                        placeholder="Search by user or group name…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>

                                <div className="acv-thread-list">
                                    {loadingT && <div className="acv-empty">Loading threads…</div>}
                                    {!loadingT && threads.length === 0 && (
                                        <div className="acv-empty">No conversations found.</div>
                                    )}

                                    {threads.map(t => {
                                        const isActive = active?.thread_id === t.thread_id;
                                        const title = t.title || t.members.map(m => m.name).join(', ').slice(0, 60);
                                        const memberCount = t.members.length;

                                        return (
                                            <button
                                                key={t.thread_id}
                                                className={`acv-thread ${isActive ? 'is-active' : ''}`}
                                                onClick={() => setActive(t)}
                                            >
                                                <div className="acv-thread-row">
                                                    <div className="acv-thread-type">
                                                        {t.thread_type === 'group' ? '👥 Group' : '💬 DM'}
                                                    </div>
                                                    <div className="acv-thread-time">
                                                        {formatDateTime(t.last_message_at)}
                                                    </div>
                                                </div>

                                                <div className="acv-thread-title">{title}</div>

                                                <div className="acv-thread-preview">
                                                    {t.last_message_body || '—'}
                                                </div>

                                                <div className="acv-thread-members">
                                                    {memberCount} member{memberCount !== 1 ? 's' : ''}
                                                    <span className="acv-thread-key">#{t.thread_key}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </aside>

                            {/* ------- Message pane ------- */}
                            <section className="acv-window">
                                {!active && (
                                    <div className="acv-window-empty">
                                        <div className="acv-window-empty-icon">🔒</div>
                                        <h3>Select a conversation</h3>
                                        <p>Pick any thread on the left to view its messages. All conversations are visible to Super Admins for security auditing.</p>
                                    </div>
                                )}

                                {active && (
                                    <>
                                        <header className="acv-window-head">
                                            <div>
                                                <strong>
                                                    {active.title || active.members.map(m => m.name).join(', ')}
                                                </strong>
                                                <span className="acv-window-head-sub">
                                                    {active.thread_type === 'group' ? 'Group conversation' : 'Direct message'}
                                                    {' • '}
                                                    {active.members.map(m => m.name).join(', ')}
                                                </span>
                                            </div>
                                            <div className="acv-window-head-meta">
                                                <span>Thread ID: {active.thread_id}</span>
                                                <span>Key: {active.thread_key}</span>
                                            </div>
                                        </header>

                                        <div className="acv-messages">
                                            {loadingM && <div className="acv-empty">Loading messages…</div>}
                                            {!loadingM && messages.length === 0 && (
                                                <div className="acv-empty">No messages in this thread yet.</div>
                                            )}

                                            {messages.map(m => {
                                                const mine = m.sender.id === meId;
                                                return (
                                                    <div key={m.id} className={`acv-msg ${mine ? 'is-mine' : 'is-theirs'}`}>
                                                        <div className="acv-msg-avatar">
                                                            {m.sender.image
                                                                ? <img src={m.sender.image} alt={m.sender.name} />
                                                                : <span>{m.sender.name.charAt(0).toUpperCase()}</span>}
                                                        </div>

                                                        <div className="acv-msg-content">
                                                            <div className="acv-msg-head">
                                                                <strong>{m.sender.name}</strong>
                                                                <span className="acv-msg-type">({m.sender.usertype})</span>
                                                                <span className="acv-msg-time">{formatBubbleTime(m.created_at)}</span>
                                                            </div>

                                                            <div className="acv-msg-bubble">
                                                                {m.attachment && (
                                                                    <div className="acv-msg-attach">
                                                                        {m.attachment.type?.startsWith('image/') ? (
                                                                            <a href={m.attachment.url} target="_blank" rel="noreferrer">
                                                                                <img src={m.attachment.url} alt={m.attachment.name} />
                                                                            </a>
                                                                        ) : (
                                                                            <a href={m.attachment.url} target="_blank" rel="noreferrer" className="acv-msg-file">
                                                                                📎 {m.attachment.name}
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                {m.body && <div className="acv-msg-body">{m.body}</div>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminChatViewer;